import React, { useMemo, useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Plus } from 'lucide-react';

export const DEFAULT_LEAD_SOURCES = [
  'LinkedIn',
  'Referral',
  'Website',
  'Cold Outreach',
  'Email Campaign',
  'Event',
  'Webinar',
  'Phone',
  'Advertisement',
  'Partner',
];

const OTHER = '__other__';

/**
 * Canonical source dropdown. Merges DEFAULT_LEAD_SOURCES with any sources
 * already present in `existingSources` so legacy values stay selectable.
 * Picking "Other..." reveals a text input for ad-hoc values.
 *
 * Props:
 *   value            - current source string (may be "" for unset)
 *   onValueChange    - called with the chosen source string
 *   existingSources  - optional array of strings already used in the data
 *   placeholder      - shown when value is empty
 */
export default function SourceSelect({ value, onValueChange, existingSources = [], placeholder = 'Select a source' }) {
  const options = useMemo(() => {
    const merged = new Set([...DEFAULT_LEAD_SOURCES, ...existingSources.filter(Boolean)]);
    return Array.from(merged).sort((a, b) => a.localeCompare(b));
  }, [existingSources]);

  const isCustom = !!value && !options.includes(value);
  const [otherMode, setOtherMode] = useState(isCustom);
  const [customValue, setCustomValue] = useState(isCustom ? value : '');

  useEffect(() => {
    if (isCustom) {
      setOtherMode(true);
      setCustomValue(value);
    }
  }, [value, isCustom]);

  const selectValue = otherMode ? OTHER : (value || '');

  return (
    <div className="space-y-2">
      <Select
        value={selectValue}
        onValueChange={(v) => {
          if (v === OTHER) {
            setOtherMode(true);
            onValueChange(customValue || '');
          } else {
            setOtherMode(false);
            setCustomValue('');
            onValueChange(v);
          }
        }}
      >
        <SelectTrigger data-testid="source-select">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(opt => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
          <SelectItem value={OTHER} className="text-indigo-600 font-medium border-t mt-1 pt-1">
            <span className="flex items-center gap-1"><Plus className="h-3 w-3" /> Other...</span>
          </SelectItem>
        </SelectContent>
      </Select>

      {otherMode && (
        <Input
          autoFocus
          placeholder="Enter custom source"
          value={customValue}
          onChange={(e) => { setCustomValue(e.target.value); onValueChange(e.target.value); }}
          data-testid="source-custom-input"
        />
      )}
    </div>
  );
}
